/**
 * Slack-style workspace switcher: current workspace label, list, inline create.
 * variant sidebarHeader: sits in Sidebar top row (no outer border; tighter padding).
 */
import { homeFor } from '@stepflow/ui-shared/utils/lastPlace.js';
import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2 } from 'lucide-react';
import { useAuth } from '@stepflow/ui-shell/context/AuthContext.jsx';
import PortalMenu from '@stepflow/ui-shared/components/PortalMenu.jsx';
import { canAccessNetworkWorkbench } from '@/utils/stepflowRoles';
import { alertDialog } from '@stepflow/ui-shared/components/DialogHost.jsx';

export default function WorkspaceSwitcher({ variant = 'default', compact = false, menuAlign = 'bottom-left', rail = false }) {
  const { workspaces, pendingInvites, activeTenantId, switchWorkspace, createWorkspace } = useAuth();
  const [open, setOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const rootRef = useRef(null);
  const topbarBtnRef = useRef(null);
  const topbarListRef = useRef(null);   // the PortalMenu'd list (outside rootRef in the DOM)
  const navigate = useNavigate();

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (topbarListRef.current && topbarListRef.current.contains(e.target)) return;
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const active =
    workspaces.find((w) => w.tenantId === activeTenantId) || workspaces[0] || null;
  const baseLabel = active?.name || active?.tenantId || 'Select workspace';
  // Show the tenant id in brackets alongside the name (skip if the label
  // already IS the tenant id, i.e. no name).
  const label =
    active?.name && active?.tenantId && active.name !== active.tenantId
      ? `${baseLabel} (${active.tenantId})` : baseLabel;   // a workspace with no real name shows its id ONCE

  const handleSelect = async (tid) => {
    if (!tid || tid === activeTenantId) {
      setOpen(false);
      return;
    }
    const r = await switchWorkspace(tid);
    setOpen(false);
    if (r?.success !== false) {
      // WHERE YOU LEFT OFF in that workspace (§8a), else its home
      const can = canAccessNetworkWorkbench(r.workspaceRole);
      navigate(homeFor(tid, { canWorkbench: can, fallback: can ? '/network/new' : '/agentic' }));
    }
  };

  const openPendingJoin = (token) => {
    const t = (token || '').trim();
    if (!t) return;
    setOpen(false);
    navigate(`/join?token=${encodeURIComponent(t)}`);
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    const n = newName.trim();
    if (!n || creating) return;
    setCreating(true);
    try {
      await createWorkspace(n);
      setNewName('');
      setOpen(false);
      navigate('/network/new');
    } catch (err) {(await alertDialog(err.message || 'Could not create workspace'));
    } finally {
      setCreating(false);
    }
  };

  const isHeader = variant === 'sidebarHeader';
  // Low-profile inline variant for the global top bar: text + chevron only,
  // no card/border, minimal footprint, left-aligned within the bar.
  const isTopbar = variant === 'topbar';

  if (isTopbar) {
    return (
      <div className="relative" ref={rootRef}>
        <button
          type="button"
          ref={topbarBtnRef}
          onClick={() => setOpen(!open)}
          // ``rail``: sized + styled like its rail siblings (h-10 w-10, a 20px stroke icon)
          className={rail ? `flex h-10 w-10 shrink-0 items-center justify-center rounded-xl transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400/40 ${open ? 'bg-white text-blue-600 shadow-sm ring-1 ring-gray-200/80' : 'text-gray-500 hover:bg-white/80 hover:text-gray-800'}` : compact
            ? 'inline-flex h-6 w-6 items-center justify-center rounded text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-700 focus:outline-none focus:ring-1 focus:ring-blue-400/40'
            : 'flex max-w-[220px] items-center gap-1 rounded px-1.5 py-0.5 text-left text-[11px] text-slate-600 transition-colors hover:bg-slate-200/60 hover:text-slate-800 focus:outline-none focus:ring-1 focus:ring-blue-400/40'}
          aria-expanded={open}
          aria-haspopup="listbox"
          aria-label={compact || rail ? `Switch workspace — ${label}` : undefined}
          title={compact || rail ? `Switch workspace — ${label}` : 'Switch workspace'}
          data-testid={rail ? 'rail-workspace-button' : undefined}
        >
          {rail ? <Building2 className="h-5 w-5" strokeWidth={1.75} aria-hidden /> : compact ? (
            <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <rect x="4" y="3" width="16" height="18" rx="2" /><path d="M9 7h1M14 7h1M9 11h1M14 11h1M9 15h1M14 15h1M10 21v-3h4v3" />
            </svg>
          ) : <span className="truncate font-medium text-slate-700">{label}</span>}
          <svg
            className={`h-3 w-3 shrink-0 text-slate-400 transition ${open ? 'rotate-180' : ''} ${compact || rail ? 'hidden' : ''}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            aria-hidden
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {/* PortalMenu (never clipped/covered — the switcher now sits in the narrow, animated sidebar) */}
        <PortalMenu anchorRef={topbarBtnRef} open={open} onClose={() => setOpen(false)} align={menuAlign}
          menuRole="presentation" width={288}
          className="max-h-80 w-72 overflow-auto rounded-lg border border-gray-200 bg-white py-1 shadow-xl">
          <div ref={topbarListRef} role="listbox">
            {workspaces.length === 0 && (!pendingInvites || pendingInvites.length === 0) ? (
              <div className="px-3 py-2 text-xs text-gray-500">No workspaces yet</div>
            ) : null}
            {workspaces.map((w) => (
              <button
                key={w.tenantId}
                type="button"
                role="option"
                aria-selected={w.tenantId === activeTenantId}
                onClick={() => handleSelect(w.tenantId)}
                className={`w-full px-3 py-2 text-left text-sm hover:bg-gray-50 ${
                  w.tenantId === activeTenantId ? 'bg-blue-50/90' : ''
                }`}
              >
                <div className="truncate font-medium text-gray-900">{w.name || w.tenantId}</div>
                <div className="truncate text-xs text-gray-500">
                  {w.role} · {w.tenantId}
                </div>
              </button>
            ))}
            {(pendingInvites || []).map((p) => (
              <button
                key={`pending-${p.token}`}
                type="button"
                role="option"
                onClick={() => openPendingJoin(p.token)}
                className="w-full border-t border-amber-100 px-3 py-2 text-left text-sm hover:bg-amber-50/80"
              >
                <div className="truncate font-medium text-amber-900">
                  {p.name || p.tenantId}{' '}
                  <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-amber-900">
                    Pending
                  </span>
                </div>
                <div className="truncate text-xs text-amber-800/90">Accept or decline on join page</div>
              </button>
            ))}
            <form
              onSubmit={handleCreate}
              className="border-t border-gray-100 p-2"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="mb-1 text-xs font-medium text-gray-600">New workspace</div>
              <input
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="e.g. Acme Corp"
                maxLength={120}
                className="w-full rounded-md border border-gray-200 px-2 py-1.5 text-sm focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-400"
              />
              <button
                type="submit"
                disabled={creating || !newName.trim()}
                className="mt-2 w-full rounded-md bg-blue-600 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
              >
                {creating ? 'Creating…' : 'Create workspace'}
              </button>
            </form>
          </div>
        </PortalMenu>
      </div>
    );
  }

  return (
    <div
      className={`relative ${isHeader ? 'min-w-0 flex-1 py-0' : 'border-b border-gray-200 px-3 pb-3 pt-1'}`}
      ref={rootRef}
    >
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className={`flex w-full items-center gap-2 rounded-lg border border-gray-200 bg-white text-left transition hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500/30 ${
          isHeader ? 'px-2.5 py-1.5 shadow-none' : 'px-3 py-2 shadow-sm'
        }`}
        aria-expanded={open}
        aria-haspopup="listbox"
      >
        <div className="min-w-0 flex-1">
          <div className="text-[10px] font-medium uppercase tracking-wide text-gray-500">
            Workspace
          </div>
          <div className="truncate text-sm font-semibold text-gray-900">{label}</div>
        </div>
        <svg
          className={`h-4 w-4 shrink-0 text-gray-500 transition ${open ? 'rotate-180' : ''}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          aria-hidden
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {open ? (
        <div
          className={`absolute top-full z-50 mt-1 max-h-80 overflow-auto rounded-lg border border-gray-200 bg-white py-1 shadow-xl ${
            isHeader ? 'left-0 right-0' : 'left-3 right-3'
          }`}
          role="listbox"
        >
          {workspaces.length === 0 && (!pendingInvites || pendingInvites.length === 0) ? (
            <div className="px-3 py-2 text-xs text-gray-500">No workspaces yet</div>
          ) : null}
          {workspaces.map((w) => (
            <button
              key={w.tenantId}
              type="button"
              role="option"
              aria-selected={w.tenantId === activeTenantId}
              onClick={() => handleSelect(w.tenantId)}
              className={`w-full px-3 py-2 text-left text-sm hover:bg-gray-50 ${
                w.tenantId === activeTenantId ? 'bg-blue-50/90' : ''
              }`}
            >
              <div className="truncate font-medium text-gray-900">
                {w.name || w.tenantId}
              </div>
              <div className="truncate text-xs text-gray-500">
                {w.role} · {w.tenantId}
              </div>
            </button>
          ))}
          {(pendingInvites || []).map((p) => (
            <button
              key={`pending-${p.token}`}
              type="button"
              role="option"
              onClick={() => openPendingJoin(p.token)}
              className="w-full border-t border-amber-100 px-3 py-2 text-left text-sm hover:bg-amber-50/80"
            >
              <div className="truncate font-medium text-amber-900">
                {p.name || p.tenantId}{' '}
                <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-amber-900">
                  Pending
                </span>
              </div>
              <div className="truncate text-xs text-amber-800/90">Accept or decline on join page</div>
            </button>
          ))}
          <form
            onSubmit={handleCreate}
            className="border-t border-gray-100 p-2"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-1 text-xs font-medium text-gray-600">New workspace</div>
            <input
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="e.g. Acme Corp"
              maxLength={120}
              className="w-full rounded-md border border-gray-200 px-2 py-1.5 text-sm focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-400"
            />
            <button
              type="submit"
              disabled={creating || !newName.trim()}
              className="mt-2 w-full rounded-md bg-blue-600 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {creating ? 'Creating…' : 'Create workspace'}
            </button>
          </form>
        </div>
      ) : null}
    </div>
  );
}
